import { RECORD_COUNTDOWN_MS } from "../config/constants.ts";
import { canRecordFromPrivacy } from "../privacy/privacyMath.ts";
import { selectRecordingMime, timestampedFilename } from "./mime.ts";

export interface RecordGate {
  cameraActive: boolean;
  concealmentApplied: boolean;
  ready: boolean;
  unsafe: boolean;
}

export class RecordingCompositor {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private objectUrl: string | null = null;
  private lastBlob: Blob | null = null;
  private lastMime: string | null = null;
  private startedAt = 0;
  recording = false;
  countdown = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d", {
      alpha: false,
      colorSpace: "srgb",
    } as CanvasRenderingContext2DSettings);
    if (!ctx) throw new Error("Recording compositor canvas is unavailable.");
    this.ctx = ctx;
  }

  canStart(gate: RecordGate): { ok: boolean; reason?: string } {
    if (typeof MediaRecorder === "undefined") {
      return { ok: false, reason: "This browser does not support MediaRecorder." };
    }
    return canRecordFromPrivacy(gate);
  }

  async start(options: {
    gate: RecordGate;
    audioStream?: MediaStream | null;
    fps?: number;
    onCountdown?: (n: number) => void;
  }): Promise<void> {
    const allowed = this.canStart(options.gate);
    if (!allowed.ok) throw new Error(allowed.reason);
    const mime = selectRecordingMime();
    this.lastMime = mime;
    for (let n = 3; n >= 1; n -= 1) {
      this.countdown = n;
      options.onCountdown?.(n);
      await sleep(RECORD_COUNTDOWN_MS / 3);
    }
    this.countdown = 0;
    options.onCountdown?.(0);

    const fps = options.fps ?? 30;
    const canvasStream = this.canvas.captureStream(fps);
    const tracks: MediaStreamTrack[] = [...canvasStream.getVideoTracks()];
    if (options.audioStream) tracks.push(...options.audioStream.getAudioTracks());
    const stream = new MediaStream(tracks);
    this.chunks = [];
    this.recorder = mime
      ? new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 8_000_000 })
      : new MediaRecorder(stream);
    this.recorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.chunks.push(event.data);
    };
    this.recorder.start(250);
    this.startedAt = performance.now();
    this.recording = true;
  }

  elapsedSeconds(): number {
    if (!this.recording) return 0;
    return (performance.now() - this.startedAt) / 1000;
  }

  compose(args: {
    universe: HTMLCanvasElement;
    camera: HTMLCanvasElement;
    cameraRect: { x: number; y: number; width: number; height: number };
    overlays: OverlayDraw[];
    width: number;
    height: number;
  }): void {
    const { ctx, canvas } = this;
    if (canvas.width !== args.width || canvas.height !== args.height) {
      canvas.width = args.width;
      canvas.height = args.height;
    }
    ctx.fillStyle = "#03050a";
    ctx.fillRect(0, 0, args.width, args.height);
    ctx.drawImage(args.universe, 0, 0, args.width, args.height);
    ctx.save();
    ctx.strokeStyle = "rgba(0,240,255,0.45)";
    ctx.lineWidth = 2;
    ctx.strokeRect(
      args.cameraRect.x,
      args.cameraRect.y,
      args.cameraRect.width,
      args.cameraRect.height,
    );
    ctx.drawImage(
      args.camera,
      args.cameraRect.x,
      args.cameraRect.y,
      args.cameraRect.width,
      args.cameraRect.height,
    );
    ctx.restore();
    for (const overlay of args.overlays) overlay(ctx, args.width, args.height);
  }

  async stop(): Promise<Blob | null> {
    const recorder = this.recorder;
    if (!recorder) return this.lastBlob;
    const blob = await new Promise<Blob>((resolve) => {
      recorder.onstop = () => {
        resolve(new Blob(this.chunks, { type: this.lastMime ?? "video/webm" }));
      };
      if (recorder.state !== "inactive") recorder.stop();
      else resolve(new Blob(this.chunks, { type: this.lastMime ?? "video/webm" }));
    });
    this.recording = false;
    this.recorder = null;
    this.lastBlob = blob.size > 0 ? blob : null;
    return this.lastBlob;
  }

  download(): string | null {
    if (!this.lastBlob) return null;
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = URL.createObjectURL(this.lastBlob);
    const a = document.createElement("a");
    a.href = this.objectUrl;
    a.download = timestampedFilename(new Date(), this.lastMime);
    a.click();
    return this.objectUrl;
  }

  dispose(): void {
    if (this.recorder && this.recorder.state !== "inactive") this.recorder.stop();
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = null;
  }
}

export type OverlayDraw = (ctx: CanvasRenderingContext2D, width: number, height: number) => void;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
